import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6f2vi2sgkaik1unef0";
const MAX_MESSAGE = 500;

type Status = "idle" | "loading" | "success" | "error";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [msgLength, setMsgLength] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("提交成功，感谢您的留言！");
      form.reset();
      setMsgLength(0);
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const content = String(formData.get("message") || "").trim();

    if (!name || !email || !content) {
      setStatus("error");
      setMessage("请完整填写姓名、邮箱与留言内容。");
      return;
    }
    if (content.length > MAX_MESSAGE) {
      setStatus("error");
      setMessage(`留言内容请控制在 ${MAX_MESSAGE} 字以内。`);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setMessage("请输入有效的邮箱地址。");
      return;
    }

    setStatus("loading");
    setMessage("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setMessage(serverMsg ? String(serverMsg) : "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setMessage("提交成功，感谢您的留言！我们会尽快与您联系。");
      form.reset();
      setMsgLength(0);
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <form
      id="senzhuang-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="p-5 md:p-7 rounded-lg bg-background-50 border border-background-200"
      noValidate
    >
      <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-1">在线留言</h2>
      <p className="text-sm text-foreground-500 mb-6">
        填写下方表单与森泽佳奈的官方团队取得联系（演示用途）。
      </p>

      <div className="flex flex-col gap-4">
        <div>
          <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">
            姓名 <span className="text-accent-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            placeholder="请输入您的姓名"
            autoComplete="name"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="请输入常用邮箱"
            autoComplete="email"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">
            留言内容 <span className="text-accent-600">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={5}
            maxLength={MAX_MESSAGE}
            placeholder="请输入您的留言（最多 500 字）"
            onChange={(e) => setMsgLength(e.target.value.length)}
            className={`${inputClass} resize-none leading-relaxed`}
          />
          <div className="text-right text-xs text-foreground-400 mt-1">
            {msgLength} / {MAX_MESSAGE}
          </div>
        </div>

        <div className="form-honeypot-guard" aria-hidden="true">
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>

        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill"></i>
              提交留言
            </>
          )}
        </button>

        {message && (
          <div
            className={`flex items-start gap-2 px-4 py-3 rounded-md text-sm ${
              status === "success"
                ? "bg-accent-100 text-accent-900"
                : "bg-primary-100 text-primary-800"
            }`}
          >
            <i
              className={`${
                status === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"
              } mt-0.5`}
            ></i>
            <span>{message}</span>
          </div>
        )}
      </div>
    </form>
  );
}